import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { authApi, tokenStorage, type UserProfile } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";

interface AuthContextType {
  user: UserProfile | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: unknown }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: unknown }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast }             = useToast();

  // Restaurar sessão ao carregar a página
  useEffect(() => {
    const token = tokenStorage.get();
    if (token) {
      authApi.me()
        .then((u) => setUser(u))
        .catch(() => tokenStorage.clear())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { token, user: u } = await authApi.login(email, password);
      tokenStorage.set(token);
      setUser(u);
      toast({ title: "Sucesso!", description: "Login realizado com sucesso." });
      return { error: null };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao fazer login.";
      toast({ title: "Erro no login", description: msg, variant: "destructive" });
      return { error: err };
    }
  };

  const signUp = async (email: string, password: string, name?: string) => {
    try {
      const { token, user: u } = await authApi.register(email, password, name);
      tokenStorage.set(token);
      setUser(u);
      toast({ title: "Cadastro realizado!", description: "Bem-vindo!" });
      return { error: null };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao criar conta.";
      toast({ title: "Erro no cadastro", description: msg, variant: "destructive" });
      return { error: err };
    }
  };

  const signOut = async () => {
    tokenStorage.clear();
    setUser(null);
    toast({ title: "Logout realizado", description: "Você foi desconectado." });
  };

  const value: AuthContextType = {
    user,
    profile: user,
    loading,
    signIn,
    signUp,
    signOut,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
